import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Home,
  Dog,
  Calendar,
  FileText,
  CreditCard,
  User,
  Users,
  Settings,
  ClipboardList,
  ClipboardCheck,
  BarChart3,
  Receipt,
  Package,
  FileSignature,
  LogOut,
  Warehouse,
  UserCog,
  ArrowLeftRight,
  Mail,
} from 'lucide-react'
import { Logo } from '@/components/shared/logo'
import { useAuth } from '@/providers/auth-provider'

interface SidebarProps {
  isOpen: boolean
}

const customerNavItems = [
  { to: '/dashboard', icon: Home, label: 'Dashboard' },
  { to: '/pets', icon: Dog, label: 'My Pets' },
  { to: '/reservations', icon: Calendar, label: 'Reservations' },
  { to: '/invoices', icon: FileText, label: 'Invoices' },
  { to: '/profile', icon: User, label: 'Profile' },
]

interface NavItem {
  to: string
  icon: typeof Home
  label: string
}

interface NavSection {
  label: string
  items: NavItem[]
}

const adminNavSections: NavSection[] = [
  {
    label: '',
    items: [
      { to: '/admin', icon: Home, label: 'Dashboard' },
    ],
  },
  {
    label: 'Clients',
    items: [
      { to: '/admin/customers', icon: Users, label: 'Customers' },
      { to: '/admin/pets', icon: Dog, label: 'Pets' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/admin/reservations', icon: ClipboardList, label: 'Reservations' },
      { to: '/admin/run-sheet', icon: ClipboardCheck, label: 'Run Sheet' },
      { to: '/admin/calendar', icon: Calendar, label: 'Calendar' },
      { to: '/admin/kennels', icon: Warehouse, label: 'Kennels' },
    ],
  },
  {
    label: 'Billing',
    items: [
      { to: '/admin/invoices', icon: FileText, label: 'Invoices' },
      { to: '/admin/receivables', icon: Receipt, label: 'Receivables' },
      { to: '/admin/payments', icon: CreditCard, label: 'Payments' },
      { to: '/admin/reports', icon: BarChart3, label: 'Reports' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { to: '/admin/services', icon: Package, label: 'Services' },
      { to: '/admin/agreements', icon: FileSignature, label: 'Agreements' },
      { to: '/admin/emails', icon: Mail, label: 'Emails' },
      { to: '/admin/staff', icon: UserCog, label: 'Team' },
      { to: '/admin/settings', icon: Settings, label: 'Settings' },
    ],
  },
]


export function Sidebar({ isOpen }: SidebarProps) {
  const { isStaff, signOut, profile } = useAuth()
  const location = useLocation()
  const isOnAdminSide = location.pathname.startsWith('/admin')

  const isActive = (path: string) => {
    if (path === '/admin' && location.pathname === '/admin') return true
    if (path === '/dashboard' && location.pathname === '/dashboard') return true
    if (path !== '/admin' && path !== '/dashboard') {
      return location.pathname.startsWith(path)
    }
    return false
  }

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen bg-sidebar border-r border-sidebar-border transition-all duration-300',
        isOpen ? 'w-64' : 'w-16'
      )}
    >
      <div className="flex h-full flex-col">
        <div className="flex h-16 items-center px-4 border-b border-sidebar-border">
          <Logo collapsed={!isOpen} className={isOpen ? 'h-10' : 'h-8'} />
        </div>

        <ScrollArea className="flex-1 py-4">
          {isOnAdminSide ? (
            <nav className="space-y-4 px-2">
              {adminNavSections.map((section) => (
                <div key={section.label || '_top'}>
                  {section.label && isOpen && (
                    <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-sidebar-primary">
                      {section.label}
                    </p>
                  )}
                  {section.label && !isOpen && (
                    <Separator className="my-2 mx-auto w-8" />
                  )}
                  <div className="space-y-1">
                    {section.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={() =>
                          cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                            isActive(item.to)
                              ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                              : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                          )
                        }
                      >
                        <item.icon className="h-5 w-5 shrink-0" />
                        {isOpen && <span>{item.label}</span>}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          ) : (
            <nav className="space-y-1 px-2">
              {customerNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={() =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive(item.to)
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    )
                  }
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  {isOpen && <span>{item.label}</span>}
                </NavLink>
              ))}
            </nav>
          )}
        </ScrollArea>

        <div className="border-t border-sidebar-border p-4">
          {isOpen && profile && (
            <div className="mb-3 px-2">
              <p className="text-sm font-medium text-sidebar-foreground truncate">
                {profile.first_name} {profile.last_name}
              </p>
              <p className="text-xs text-muted-foreground truncate">{profile.email}</p>
            </div>
          )}
          <Separator className="mb-3" />
          {isStaff && (
            <NavLink
              to={isOnAdminSide ? '/dashboard' : '/admin'}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground mb-1',
                !isOpen && 'justify-center px-0'
              )}
            >
              <ArrowLeftRight className="h-5 w-5 shrink-0" />
              {isOpen && <span>{isOnAdminSide ? 'Customer View' : 'Admin Panel'}</span>}
            </NavLink>
          )}
          <Button
            variant="ghost"
            className={cn(
              'w-full justify-start gap-3 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              !isOpen && 'justify-center px-0'
            )}
            onClick={signOut}
          >
            <LogOut className="h-5 w-5" />
            {isOpen && <span>Sign Out</span>}
          </Button>
        </div>
      </div>
    </aside>
  )
}
